/**
 * GTM `bookFreeTrial` conversion event.
 *
 * Pushed the moment a free-trial booking is confirmed by the backend. Unlike
 * the `signUp` event (see gtmSignup.ts), this needs no sessionStorage flash:
 * the booking success screen renders in place on /book/free-trial, which sits
 * in the `(web)` route group and already carries the container, so there is no
 * page handoff to survive.
 *
 * Client-side rather than server-side on purpose. A free trial is a marketing
 * lead, and its value is in attributing it to the campaign that produced it -
 * gclid, GA client_id, referrer and landing page all live in the browser.
 * Reporting it from Laravel would mean either losing that context or
 * forwarding client_id by hand. The tradeoff accepted is that ad blockers drop
 * some events; if booking counts ever need to reconcile exactly with the
 * database, this is the event to move server-side.
 */

import { buildGtmSignupStudent, type GtmSignupStudent } from "./gtmSignup";

/**
 * The booked slot, as GTM sees it. Names are a contract with the container -
 * the tag reads these through Data Layer Variables, so renaming one here
 * silently breaks it on the other side.
 */
export type GtmFreeTrialBooking = {
  category: string;
  language: string;
  teacherId: string;
  startUtc: string;
  endUtc: string;
};

type DataLayerWindow = Window & { dataLayer?: unknown[] };

function str(value: unknown): string {
  if (value == null) {
    return "";
  }
  return typeof value === "string" ? value : String(value);
}

/**
 * `student` mirrors the `signUp` event's shape exactly, so a container
 * variable written for one works unchanged on the other. It describes the
 * account holder who booked - for a dependant's trial that is still the parent
 * account, which is the entity marketing attributes the lead to.
 */
export function pushGtmFreeTrialBooking(args: {
  user: unknown;
  category: unknown;
  language: unknown;
  teacherId: unknown;
  startUtc: unknown;
  endUtc: unknown;
}): void {
  if (typeof window === "undefined") {
    return;
  }

  const student: GtmSignupStudent = buildGtmSignupStudent(args.user);
  const booking: GtmFreeTrialBooking = {
    category: str(args.category),
    language: str(args.language),
    teacherId: str(args.teacherId),
    startUtc: str(args.startUtc),
    endUtc: str(args.endUtc),
  };

  const w = window as DataLayerWindow;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: "bookFreeTrial",
    student: [student],
    booking,
  });
}
